import styled from '@emotion/styled';
import {
    Button,
    Form,
    FormInputRadioGroup,
    pxToRem,
    useForm,
} from '@modernhealth/vital-components';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { color, font } from 'src/shared/styles/variables';

const QuestionText = styled.h4(
    {
        color: color.ebonyClay,
        marginTop: 15,
        marginBottom: 5,
        textAlign: 'center',
    },
    font.black(font.size.extraLarge),
);

const ShareFeedbackText = styled.p(
    {
        color: color.baliHai,
        marginTop: 20,
        textAlign: 'center',
    },
    font.medium(font.size.mediumExtended),
);

const AnswersWrapper = styled.ul({
    display: 'flex',
    flexWrap: 'wrap',
    marginTop: 25,
    padding: 0,
    justifyContent: 'space-between',
    width: '100%',
});

interface Props {
    onContinueClick: () => void;
    question: string;
    answersOptions: { label: string; value: string }[];
    onSkipClick: () => void;
    isDisabled?: boolean;
    onAnswerClick: (answer: string) => void;
}

interface FormData {
    answer: string;
}

export const RateSessionMCQuestion: React.FC<Props> = ({
    onContinueClick,
    onSkipClick,
    onAnswerClick,
    question,
    isDisabled,
    answersOptions,
}) => {
    const { t } = useTranslation();

    const formDefinition = useForm<FormData>();

    const handleSubmit = (data: FormData) => {
        onAnswerClick(data.answer);
        onContinueClick();
    };

    useEffect(() => {
        const subscription = formDefinition.watch((values, { name, type }) => {
            if (name === 'answer' && type === 'change') {
                onAnswerClick(values.answer as string);
            }
        });
        return () => subscription.unsubscribe();
    }, [formDefinition, onAnswerClick]);

    return (
        <React.Fragment>
            <Form definition={formDefinition} onSubmit={handleSubmit} css={{ gap: 0 }}>
                <ShareFeedbackText id="mc-question-label">
                    {t('videoSessions:rate_session.tile_question.title')}
                </ShareFeedbackText>
                <QuestionText>{question}</QuestionText>

                <AnswersWrapper>
                    <FormInputRadioGroup
                        options={answersOptions}
                        labelledBy="mc-question-label"
                        name="answer"
                    />
                </AnswersWrapper>

                <Button onClick={onSkipClick} style={{ marginTop: pxToRem(60) }} fluid>
                    {t('common.skip')}
                </Button>
                <Button
                    type="submit"
                    style={{ marginTop: pxToRem(10) }}
                    disabled={isDisabled}
                    fluid
                >
                    {t('common.continue')}
                </Button>
            </Form>
        </React.Fragment>
    );
};
